import { useState, useEffect } from 'react';
import {
  Card, Row, Col, Statistic, Alert, Tabs, DatePicker, Button, Empty, Tag, Space,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react';
import dayjs from 'dayjs';
import { prediction } from '../../../services/api';

/**
 * 预测中心：负荷预测 / 光伏预测
 * 数据来源标注（data.source）：
 *  - ai-service-live  实时调用 ai-service 计算
 *  - database-cache   预测服务不可用时的降级缓存结果（可能带 stale:true）
 * method === 'insufficient_data' 或 data_sufficient === false 时只显示"数据积累中"，不显示曲线与精度；
 * mape 为 null 表示样本不足无法回测，严禁编造精度数字。
 */

// 单个预测面板（负荷/光伏/电价共用一套渲染逻辑，仅接口、单位与配色不同）
function ForecastPanel({
  title, color, fetcher,
  unit = 'kW', totalLabel = '当日预测总量', totalSuffix = 'kWh',
  seriesName = '预测功率', noMapeText = '样本不足，暂无法评估精度',
}) {
  const [date, setDate] = useState(dayjs());
  const [data, setData] = useState(null);
  const [fallbackMsg, setFallbackMsg] = useState(''); // data 为 null 时后端返回的说明
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try {
      const res = await fetcher({ date: d.format('YYYY-MM-DD') });
      setData(res.data || null);
      setFallbackMsg(res.data ? '' : (res.message || '暂无预测结果'));
    } catch (err) {
      // 拦截器已统一提示
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(dayjs()); }, []);

  const handleDateChange = (d) => {
    if (!d) return;
    setDate(d);
    load(d);
  };

  // 禁止选择未来 7 天以后的日期
  const disabledDate = (current) => current && current.isAfter(dayjs().add(7, 'day').endOf('day'));

  // 数据不足：只提示"数据积累中"，不显示曲线和精度
  const insufficient = data && (data.method === 'insufficient_data' || data.data_sufficient === false);
  const hasCurve = data && !insufficient && (data.hourly || []).length > 0;

  // 24h 预测曲线（折线面积图 + 上下限虚线）
  // 后端 hourly 字段为 { hour, predicted, upper, lower }
  const chartOption = () => ({
    tooltip: { trigger: 'axis' },
    legend: { data: [seriesName, '上限', '下限'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: data.hourly.map((h) => String(h.hour)) },
    yAxis: { type: 'value', name: unit },
    series: [
      {
        name: seriesName,
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: data.hourly.map((h) => h.predicted),
        itemStyle: { color },
        areaStyle: { color, opacity: 0.15 },
      },
      {
        name: '上限',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: data.hourly.map((h) => h.upper),
        itemStyle: { color: '#bbb' },
        lineStyle: { type: 'dashed', width: 1 },
      },
      {
        name: '下限',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: data.hourly.map((h) => h.lower),
        itemStyle: { color: '#bbb' },
        lineStyle: { type: 'dashed', width: 1 },
      },
    ],
  });

  return (
    <Card
      title={(
        <Space>
          {title}
          {data?.source === 'database-cache' && (
            <Tag color="orange">缓存结果（预测服务暂不可用）</Tag>
          )}
        </Space>
      )}
      size="small"
      extra={(
        <Space>
          <DatePicker
            value={date}
            allowClear={false}
            disabledDate={disabledDate}
            onChange={handleDateChange}
          />
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => load(date)}>重新预测</Button>
        </Space>
      )}
    >
      {!data && !loading && <Empty description={fallbackMsg} />}

      {insufficient && (
        <Alert
          type="info"
          showIcon
          message="数据积累中"
          description="历史运行数据尚不足以生成可靠预测，请等待数据积累后再查看。"
        />
      )}

      {hasCurve && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Card size="small">
                <Statistic title={totalLabel} value={data.total_predicted ?? '-'} suffix={totalSuffix} precision={2} />
              </Card>
            </Col>
            <Col span={16}>
              <Card size="small">
                {/* mape 为 null 时诚实说明原因，不编造精度 */}
                {data.mape != null ? (
                  <Space direction="vertical" size={4}>
                    <Statistic title="回测偏差 MAPE" value={data.mape} suffix="%" precision={2} />
                    <span style={{ color: '#52c41a' }}>准确率约 {(100 - data.mape).toFixed(2)}%</span>
                  </Space>
                ) : (
                  <Statistic title="回测精度" value={noMapeText} valueStyle={{ fontSize: 16, color: '#999' }} />
                )}
              </Card>
            </Col>
          </Row>
          <ReactEChartsCore option={chartOption()} style={{ height: 320 }} notMerge />
        </>
      )}
    </Card>
  );
}

export default function VppForecast() {
  const [serviceOk, setServiceOk] = useState(true);

  // 顶部服务状态：ai-service 不可用时黄色警告
  useEffect(() => {
    (async () => {
      try {
        const res = await prediction.status();
        const { ai_service_enabled, health } = res.data || {};
        setServiceOk(!!ai_service_enabled && health?.status === 'ok');
      } catch (err) {
        // 拦截器已统一提示
      }
    })();
  }, []);

  return (
    <div>
      {!serviceOk && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message="预测服务不可用，显示缓存结果"
          description="ai-service 当前不可用或未启用，页面展示的为最近一次已保存的预测结果。"
        />
      )}
      <Tabs
        items={[
          {
            key: 'load',
            label: '负荷预测',
            children: <ForecastPanel title="负荷预测" color="#1890ff" fetcher={prediction.load} />,
          },
          {
            key: 'pv',
            label: '光伏预测',
            children: <ForecastPanel title="光伏预测" color="#fa8c16" fetcher={prediction.pv} />,
          },
          {
            key: 'price',
            label: '电价预测',
            children: (
              <ForecastPanel
                title="电价预测（分时电价规则基线）"
                color="#52c41a"
                fetcher={prediction.price}
                unit="元/kWh"
                totalLabel="当日均价"
                totalSuffix="元/kWh"
                seriesName="预测电价"
                noMapeText="分时电价规则基线，暂无历史电价可回测"
              />
            ),
          },
        ]}
      />
    </div>
  );
}
